import React, { useMemo } from 'react';
import { Typography, Container } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import UnifiedModal from '../components/portal/common/UnifiedModal';
import LoginForm from '../components/portal/auth/LoginForm';
import RegisterForm from '../components/portal/auth/RegisterForm';
import StudentRegisterForm from '../components/portal/auth/StudentRegisterForm';
import heroDashboard from '../assets/hero-2.jpg';
import {
  PageWrap,
  Hero,
  HeroBgImage,
  HeroGrid,
  HeroTextSide,
  HeroVisualSide,
  HeroMiddleText,
  PricingBadge,
  BenefitItem,
  HeroIntroCta,
  HeroBadge,
  AuthModalContent,
  AuthModalHeader,
  RegisterModalLayout,
  RegisterModalVisual,
  RegisterModalFormSide,
  MobileCloseButton,
  TrustSection,
  StudentSection,
  StudentTextSide,
  SectionTag,
  PrimaryCta,
  ReferralBox,
  HowSection,
  HowIntro,
  HowTitle,
  HowGrid,
  HowCard,
  HowIcon,
  HowCardTitle,
  HowCardDesc,
  FinalCta,
  HeroVisualImg,
  RegisterContent,
  StudentRegisterFormSide,
  BenefitListContainer,
} from './HomeLandingPage.styles';

// ...imports...

export default function HomeLandingPage() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const authParam = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('auth');
    }, [location.search]);

    const isLoginModalOpen = authParam === 'login';
    const isRegisterModalOpen = authParam === 'register';
    const isStudentRegisterModalOpen = authParam === 'student-register';

    const closeAuthModal = () => {
        const params = new URLSearchParams(location.search);
        params.delete('auth');
        const newSearch = params.toString();
        navigate({
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : '',
        }, { replace: true });
    };

    const openLogin = () => {
        const params = new URLSearchParams(location.search);
        params.set('auth', 'login');
        navigate({ search: `?${params.toString()}` });
    };

    const openRegister = () => {
        const params = new URLSearchParams(location.search);
        params.set('auth', 'register');
        navigate({ search: `?${params.toString()}` });
    };

    const openStudentRegister = () => {
        const params = new URLSearchParams(location.search);
        params.set('auth', 'student-register');
        navigate({ search: `?${params.toString()}` });
    };

    return (
        <PageWrap>
            <Hero>
                <HeroBgImage />
                <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 2, md: 2.5, lg: 0 } }}>
                    <HeroGrid>
                        <HeroTextSide>
                            <HeroMiddleText className="reveal delay-1">
                                {t('home.benefits.title')}
                            </HeroMiddleText>

                            <PricingBadge className="reveal delay-1">
                                {t('home.benefits.price')}
                            </PricingBadge>
                            
                            <BenefitListContainer className="reveal delay-2">
                                <BenefitItem className="reveal delay-2">
                                    <div className="check">✓</div>
                                    <div className="content">
                                        <h4>{t('home.benefits.items.0.title')}</h4>
                                        <p>{t('home.benefits.items.0.desc')}</p>
                                    </div>
                                </BenefitItem>
                                <BenefitItem className="reveal delay-3">
                                    <div className="check">✓</div>
                                    <div className="content">
                                        <h4>{t('home.benefits.items.1.title')}</h4>
                                        <p>{t('home.benefits.items.1.desc')}</p>
                                    </div>
                                </BenefitItem>
                                <BenefitItem className="reveal delay-4">
                                    <div className="check">✓</div>
                                    <div className="content">
                                        <h4>{t('home.benefits.items.2.title')}</h4>
                                        <p>{t('home.benefits.items.2.desc')}</p>
                                    </div>
                                </BenefitItem>
                            </BenefitListContainer>

                            <HeroIntroCta as="button" onClick={openRegister} className="reveal delay-5">
                                {t('home.how.cta')}
                                <span className="arrow">→</span>
                            </HeroIntroCta>
                        </HeroTextSide>
                        <HeroVisualSide className="reveal delay-1">
                             <HeroVisualImg src={heroDashboard} alt="Platform Preview" />
                             <HeroBadge className="floating-1">
                                 <VerifiedRoundedIcon sx={{ fontSize: 24 }} />
                                 <span>{t('home.features.cbr')}</span>
                             </HeroBadge>
                             <HeroBadge className="floating-2">
                                 <ScheduleRoundedIcon sx={{ fontSize: 24 }} />
                                 <span>{t('home.features.scheduling')}</span>
                             </HeroBadge>
                        </HeroVisualSide>
                    </HeroGrid>
                </Container>
            </Hero>

            {/* Login Modal */}
            <UnifiedModal
                open={isLoginModalOpen}
                onClose={closeAuthModal}
                variant="center"
                size="sm"
                showClose={false}
            >
                <AuthModalContent>
                    <AuthModalHeader>
                        <h2>{t('portal.login.submit', 'Inloggen')}</h2>
                        <p>{t('portal.login.username_placeholder', 'Log in op je account')}</p>
                    </AuthModalHeader>
                    <LoginForm 
                        onRegister={openRegister}
                        onForgotPassword={() => navigate('/portal/forgot-password')}
                    />
                </AuthModalContent>
            </UnifiedModal>

            {/* Register Modal */}
            <UnifiedModal
                open={isRegisterModalOpen}
                onClose={closeAuthModal}
                variant="center"
                size="lg"
                showClose={false}
            >
                <RegisterModalLayout>
                    <RegisterModalVisual>
                        <RegisterContent>
                            <h2>{t('home.register.welcome', 'Leuk dat je DaaS wilt proberen!')}</h2>
                            <BenefitListContainer>
                                <BenefitItem>
                                    <div className="check">✓</div>
                                    <div className="content">
                                        <h4>{t('home.benefits.items.0.title', 'Slimme Planning')}</h4>
                                        <p>{t('home.benefits.items.0.desc', 'Beheer effectief lesmomenten en de beschikbaarheid van docenten.')}</p>
                                    </div>
                                </BenefitItem>
                                <BenefitItem>
                                    <div className="check">✓</div>
                                    <div className="content">
                                        <h4>{t('home.benefits.items.1.title', 'Geautomatiseerde Facturatie')}</h4>
                                        <p>{t('home.benefits.items.1.desc', 'Word sneller betaald met geïntegreerde facturatie en het bijhouden van betalingen.')}</p>
                                    </div>
                                </BenefitItem>
                                <BenefitItem>
                                    <div className="check">✓</div>
                                    <div className="content">
                                        <h4>{t('home.benefits.items.2.title', 'CBR Connectiviteit')}</h4>
                                        <p>{t('home.benefits.items.2.desc', 'Importeer examens en resultaten direct in uw workflow.')}</p>
                                    </div>
                                </BenefitItem>
                            </BenefitListContainer>
                        </RegisterContent>
                    </RegisterModalVisual>
                    <RegisterModalFormSide>
                        <MobileCloseButton onClick={closeAuthModal} aria-label="Sluiten">×</MobileCloseButton>
                        <RegisterForm 
                            onSuccess={closeAuthModal} 
                            onLogin={openLogin}
                            onSwitchRole={openStudentRegister}
                        />
                    </RegisterModalFormSide>
                </RegisterModalLayout>
            </UnifiedModal>

            {/* Student Register Modal */}
            <UnifiedModal
                open={isStudentRegisterModalOpen}
                onClose={closeAuthModal}
                variant="center"
                size="lg"
                showClose={false}
            >
                <RegisterModalLayout>
                    <RegisterModalVisual>
                        <RegisterContent>
                            <h2>{t('home.student.register.welcome', 'Klaar om de weg op te gaan?')}</h2>
                            <BenefitListContainer>
                                <BenefitItem>
                                    <div className="check">✓</div>
                                    <div className="content">
                                        <h4>{t('home.student.register.benefit1.title', 'De beste rijschool')}</h4>
                                        <p>{t('home.student.register.benefit1.desc', 'Vind eenvoudig de beste rijschool die perfect bij jouw wensen en budget past.')}</p>
                                    </div>
                                </BenefitItem>
                                <BenefitItem>
                                    <div className="check">✓</div>
                                    <div className="content">
                                        <h4>{t('home.student.register.benefit2.title', 'Theorie & Praktijk')}</h4>
                                        <p>{t('home.student.register.benefit2.desc', 'Bereid je optimaal voor op je theorie-examens en volg je voortgang in je lessen.')}</p>
                                    </div>
                                </BenefitItem>
                                <BenefitItem>
                                    <div className="check">✓</div>
                                    <div className="content">
                                        <h4>{t('home.student.register.benefit3.title', 'Kijk lessen terug')}</h4>
                                        <p>{t('home.student.register.benefit3.desc', 'Verbeter je rijvaardigheid door je lessen terug te kijken en feedback te analyseren.')}</p>
                                    </div>
                                </BenefitItem>
                                <BenefitItem>
                                    <div className="check">✓</div>
                                    <div className="content">
                                        <h4>{t('home.student.register.benefit4.title', 'Eenvoudig Plannen')}</h4>
                                        <p>{t('home.student.register.benefit4.desc', 'Boek en beheer je rijlessen direct online wanneer het jou uitkomt.')}</p>
                                    </div>
                                </BenefitItem>
                            </BenefitListContainer>
                        </RegisterContent>
                    </RegisterModalVisual>
                    <StudentRegisterFormSide>
                        <MobileCloseButton onClick={closeAuthModal} aria-label="Sluiten">×</MobileCloseButton>
                        <StudentRegisterForm 
                            onSuccess={closeAuthModal} 
                            onLogin={openLogin}
                            onSwitchRole={openRegister}
                        />
                    </StudentRegisterFormSide>
                </RegisterModalLayout>
            </UnifiedModal>

            <TrustSection>
                <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 2, md: 2.5, lg: 0 } }}>
                    <div className="trust-grid">
                        <div className="trust-item reveal delay-0">
                            <strong>1000+</strong>
                            <span>{t('home.stats.instructors')}</span>
                        </div>
                        <div className="trust-item reveal delay-1">
                            <strong>25000+</strong>
                            <span>{t('home.stats.students')}</span>
                        </div>
                        <div className="trust-item reveal delay-2">
                            <strong>150+</strong>
                            <span>{t('home.stats.schools')}</span>
                        </div>
                    </div>
                </Container>
            </TrustSection>

            <StudentSection>
                <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 2, md: 2.5, lg: 0 } }}>
                    <div className="student-grid">
                        <StudentTextSide className="text-top">
                            <SectionTag className="reveal delay-0">{t('home.student.title')}</SectionTag>
                            <Typography className="desc reveal delay-2">
                                {t('home.student.desc')}
                            </Typography>
                            <PrimaryCta onClick={openStudentRegister} className="reveal delay-4">
                                {t('home.student.cta')}
                                <ArrowForwardIcon />
                            </PrimaryCta>
                        </StudentTextSide>
                        <ReferralBox className="referral-bottom reveal delay-3">
                            <h4>
                                <WorkspacePremiumRoundedIcon sx={{ color: '#ff5722' }} />
                                {t('home.student.referral_reward', 'Referral Reward')}
                            </h4>
                            <p>
                                {t('home.student.referral')}
                            </p>
                        </ReferralBox>
                    </div>
                </Container>
            </StudentSection>

            <HowSection>
                <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 2, md: 2.5, lg: 0 } }}>
                    <HowIntro>
                        <HowTitle className="reveal delay-1">{t('home.how.lead')}</HowTitle>
                    </HowIntro>
                </Container>

                <HowGrid>
                    {featureCards(t).map((card) => (
                        <HowCard key={card.title}>
                            <div className="card-image">
                                <img src={card.image} alt={card.title} />
                                <div className="overlay" />
                                <HowIcon>{card.icon}</HowIcon>
                            </div>
                            <div className="card-content">
                                <HowCardTitle>{card.title}</HowCardTitle>
                                <HowCardDesc>{card.desc}</HowCardDesc>
                            </div>
                        </HowCard>
                    ))}
                </HowGrid>
            </HowSection>


            <FinalCta>
                <Container maxWidth="md" className="container-relative" sx={{ px: { xs: 2, sm: 2, md: 2.5, lg: 0 } }}>
                    <Typography variant="h4" className="title reveal delay-1">
                        {t('home.ctaTitle')}
                    </Typography>
                    <Typography variant="h6" className="desc reveal delay-2">
                        {t('home.ctaDesc')}
                    </Typography>
                    <PrimaryCta onClick={openRegister} className="reveal delay-3">
                        {t('home.ctaButton')}
                        <ArrowForwardIcon />
                    </PrimaryCta>
        </Container>
            </FinalCta>
        </PageWrap>
    );
}


const featureCards = (t: (k: string) => string) => [
  {
    icon: <ScheduleRoundedIcon sx={{ fontSize: 36 }} />,
    title: t('home.how.cards.0.title'),
    desc: t('home.how.cards.0.desc'),
    image: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&q=80&w=800' // Real-time scheduling/calendar image
  },
  {
    icon: <GroupRoundedIcon sx={{ fontSize: 36 }} />,
    title: t('home.how.cards.1.title'),
    desc: t('home.how.cards.1.desc'),
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&q=80&w=800'
  },
  {
    icon: <VerifiedRoundedIcon sx={{ fontSize: 36 }} />,
    title: t('home.how.cards.2.title'),
    desc: t('home.how.cards.2.desc'),
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80&w=800' // Better CBR/Legal/Exam image
  },
  {
    icon: <WorkspacePremiumRoundedIcon sx={{ fontSize: 36 }} />,
    title: t('home.how.cards.3.title'),
    desc: t('home.how.cards.3.desc'),
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80&w=800'
  },
];

